import { ActionIcon, Stack, Text, Title, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Role, SaveTruckImplement, SaveTruckImplementDelete, TruckImplement, useGetTruckImplementsQuery, useSaveTruckImplementDeleteMutation } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_trucks/caminhoes/implementos/")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Implementos de Caminhões" }] }),
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const { isAdminRoot } = useSession();
  const {
    data = [],
    isFetching,
    refetch
  } = useGetTruckImplementsQuery(undefined, {
    selectFromResult: ({ data, ...state }) => {
      return {
        ...state,
        data: data ? [...data].sort((a, b) => Number(b!.id) - Number(a!.id)) : []
      };
    }
  });
  const [triggerDelete, { isLoading: isDeleting }] = useSaveTruckImplementDeleteMutation();

  const handleEdit = (body: SaveTruckImplement["args"]) => {
    MODAL_HANDLER.createEdit.truck.implementation(body);
  };

  const handleDelete = (body: SaveTruckImplementDelete["args"]) => {
    modals.openConfirmModal({
      title: "Excluir Implemento",
      children: (
        <Stack className="text-center" gap="xs">
          <Title order={4}>Tem certeza que deseja excluir este implemento?</Title>
          <Text c="dimmed">Esta ação não pode ser desfeita.</Text>
        </Stack>
      ),
      onConfirm: () => triggerDelete(body),
      cancelProps: { disabled: isDeleting },
      confirmProps: { loading: isDeleting, color: "red" },
      groupProps: { className: cn("grid! grid-cols-1 gap-[inherit] md:grid-cols-2") },
      labels: { confirm: "Deletar", cancel: "Cancelar" }
    });
  };

  const columns: GridColDef<TruckImplement>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 90,
      align: "center",
      headerAlign: "center",
      type: "number"
    },
    {
      field: "name",
      headerName: "Nome do Implemento",
      minWidth: 200,
      flex: 1,
      type: "string"
    },
    {
      field: "shaftQuantity",
      headerName: "Quantidade de Eixos",
      align: "center",
      headerAlign: "center",
      minWidth: 150,
      type: "number"
    },
    {
      field: "grossCapacity",
      headerName: "Capacidade Bruta",
      align: "center",
      headerAlign: "center",
      minWidth: 150,
      type: "number"
    },
    {
      field: "netCapacity",
      headerName: "Capacidade Líquida",
      align: "center",
      headerAlign: "center",
      minWidth: 150,
      type: "number"
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: ({ row }) => {
        const { id } = row;

        if (!isAdminRoot) return null;

        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Implemento">
              <ActionIcon variant="light" color="yellow" onClick={() => handleEdit(row)}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Excluir Implemento">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete({ id })}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between">
        <DashboardComponents.Title>Implementos de Caminhões</DashboardComponents.Title>

        <div className="flex flex-1 justify-end gap-2 max-md:*:flex-1!">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Implementos" }} onClick={refetch} loading={isFetching} />

          {isAdminRoot && (
            <Tooltip label="Cadastrar novo Implemento">
              <ActionIcon variant="filled" size="xl" onClick={() => MODAL_HANDLER.createEdit.truck.implementation()}>
                <PlusIcon />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      </div>

      <DataGridBase rows={data as TruckImplement[]} columns={columns} loading={isFetching} />
    </Stack>
  );
}
